import React, { useEffect, useState } from "react";
import { useAuth } from "../../auth/FirebaseAuth";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import Space from "../../layout/Space";

import TextField2 from "../../textfields/TextField2";
import { useSnackbar } from "notistack";
import { useAppContext } from "../../config/AppContext";
import { getOrRefreshFirebaseToken } from "../../config/getFirebaseToken";
import SmallSpinner from "../../loading/SmallSpinner";

import {
  getArrayDocDataByID,
  getArrayDocDataByRef,
  updateDocDataField,
} from "../../models/DataFunctions";
import updateDocDataFieldApi from "../../api/database/updateDocDataFieldApi";

import TextField2T from "../../textfields/TextField2T";
import Gleap from "gleap";
import { SaveBar, ToggleSettingsBrick } from "../../textfields/EasySwitch";
import TabGPT from "../../tabs/TabGPT";
import sendRTBatimentTest from "../../api/send/sendRTBatimentTest";
import {
  ATHConfig,
  DropDown,
  Org,
  RTB_RE20,
  User,
} from "../../types/basicTypes";
import { Divider, Spin } from "antd";
import PrimaryButton from "../../buttons/PrimaryButton";
import DropDownGPT03 from "../../dropdown/DropDownGTP03";
import Icon1 from "../../assets/icons/Icon";
import SexySpin from "../../assets/SexySpinner";
import CheckBoxGrey from "../../textfields/CheckBoxGrey";

interface ATHProps {
  userData: User;
  orgData: Org;
  navigateWorkspace: (path: string) => void;
  handleClickVariant: (
    message: string,
    variant: "default" | "error" | "success" | "warning" | "info" | undefined
  ) => void;
}

const ATH: React.FC<ATHProps> = ({
  userData,
  orgData,
  navigateWorkspace,
  handleClickVariant,
}) => {
  const { currentUser, loading } = useAuth();
  const [params] = useSearchParams();
  const section = params.get("section");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rtb, setRtb] = useState<RTB_RE20 | undefined>(undefined);
  const [activeTab, setActiveTab] = useState(section || "login");

  const { appState, setAppState } = useAppContext();
  const prefix = appState.dev ? appState.dev_url : appState.prod_url;
  const [ath_config, setAthConfig] = useState<ATHConfig | undefined>(undefined);

  // Fetch user, org, clients, and article data
  const handleFetchData = async () => {
    try {
      setAthConfig(orgData.config?.ath);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    if (orgData) {
      handleFetchData();
    }
  }, [orgData]);

  if (loading || !currentUser || !orgData) {
    return (
      <div className="w-full h-full overflow-auto flex flex-row gap-2 mt-3">
        <SexySpin></SexySpin>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full pl-4 pr-6 overflow-y-auto">
      <div className="flex flex-col h-fit pb-40 w-full">
        <div className="text-h1 font-bold text-left mb-4">
          Configuration des attestations
        </div>
        <div className="flex flex-row gap-4 w-full  max-w-c1k  ">
          <div className="w-full p-2 bg-neutral-100 rounded-2xl ">
            <div className="flex flex-row gap-2 items-center p-2">
              <CheckBoxGrey
                checked={ath_config?.model !== "complex"}
                onChange={(checked: boolean) => {
                  setAthConfig({
                    ...ath_config,
                    model: checked ? "simple" : "complex",
                  });
                  updateDocDataFieldApi(
                    orgData.ref!,
                    "config.ath.model",
                    checked ? "simple" : "complex",
                    prefix
                  );
                }}
              />
              <div className="text-h2">Design simple</div>{" "}
            </div>
            <img
              src="https://ik.imagekit.io/hexaflow/Copie%20de%20easy%20ath.png?updatedAt=1752324828838"
              className="w-full h-auto rounded-2xl"
            />
          </div>{" "}
          <div className="w-full p-2  bg-neutral-100 rounded-2xl h-fit ">
            <div className="flex flex-row gap-2 items-center p-2">
              <CheckBoxGrey
                checked={ath_config?.model === "complex"}
                onChange={(checked: boolean) => {
                  setAthConfig({
                    ...ath_config,
                    model: checked ? "complex" : "simple",
                  });
                  updateDocDataFieldApi(
                    orgData.ref!,
                    "config.ath.model",
                    checked ? "complex" : "simple",
                    prefix
                  );
                }}
              />
              <span className="text-h2">Design détaillé</span>
            </div>
            <img
              src="https://ik.imagekit.io/hexaflow/Copie%20de%20easy%20ath%20(1).png?updatedAt=1752324834633"
              className="w-full h-auto  rounded-2xl"
            />
            <div className="mt-2 w-fit">
              <ToggleSettingsBrick
                checked={ath_config?.special_design === "mail"}
                onChange={(checked: boolean) => {
                  updateDocDataFieldApi(
                    orgData.ref!,
                    "config.ath.special_design",
                    checked ? "mail" : "none",
                    prefix
                  );
                }}
                title="Format courrier"
                description="Utiliser le format de courrier pour les attestations"
              />
            </div>{" "}
            <div className="mt-2 w-fit">
              <ToggleSettingsBrick
                checked={ath_config?.display_logo === true}
                onChange={(checked: boolean) => {
                  updateDocDataFieldApi(
                    orgData.ref!,
                    "config.ath.display_logo",
                    checked,
                    prefix
                  );
                }}
                title="Afficher le logo de l'entreprise"
                description="Afficher le logo de l'entreprise sur les attestations"
              />
            </div>
            <div className="mt-2 w-fit">
              <ToggleSettingsBrick
                checked={ath_config?.title_centered === true}
                onChange={(checked: boolean) => {
                  updateDocDataFieldApi(
                    orgData.ref!,
                    "config.ath.title_centered",
                    checked,
                    prefix
                  );
                }}
                title="Titre majuscule centré"
                description="ATTESTATION SUR l'HONNEUR centré en haut de la page au lieu de Attestation isolants / carbone"
              />
            </div>
            <div className="mt-2 w-fit">
              <ToggleSettingsBrick
                checked={ath_config?.underline_operation === true}
                onChange={(checked: boolean) => {
                  updateDocDataFieldApi(
                    orgData.ref!,
                    "config.ath.underline_operation",
                    checked,
                    prefix
                  );
                }}
                title="Souligner l'opération"
                description="Souligner l'opération effectuée dans l'attestation"
              />
            </div>
            <div className="mt-2 w-fit">
              <ToggleSettingsBrick
                checked={ath_config?.hide_creation_date === true}
                onChange={(checked: boolean) => {
                  updateDocDataFieldApi(
                    orgData.ref!,
                    "config.ath.hide_creation_date",
                    checked,
                    prefix
                  );
                }}
                title="Masquer la date de création"
                description="Ne pas afficher la date de création de l'attestation"
              />
            </div>{" "}
            <div className="mt-2 w-fit">
              <ToggleSettingsBrick
                checked={ath_config?.all_uppercase === true}
                onChange={(checked: boolean) => {
                  updateDocDataFieldApi(
                    orgData.ref!,
                    "config.ath.all_uppercase",
                    checked,
                    prefix
                  );
                }}
                title="Mettre tout en majuscule"
                description="Mettre tout le texte de l'attestation en majuscule"
              />
            </div>
          </div>
        </div>{" "}
      </div>
    </div>
  );
};

export default ATH;
